import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { PARKI_WEBSITE } from '../common/constants';

const ParkiSettingsDropdown = ({ user }) => {
    const history = useHistory();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const shortcutsContainerRef = useRef(null);
    const shortcutsPsRef = useRef(null);
    
    // Initialize Perfect Scrollbar for shortcuts dropdown
    useEffect(() => {
        // Perfect Scrollbar will be initialized when the dropdown is shown
        return () => {
            if (shortcutsPsRef.current && shortcutsPsRef.current.destroy) {
                shortcutsPsRef.current.destroy();
                shortcutsPsRef.current = null;
            }
        };
    }, []);

    // Initialize or update Perfect Scrollbar when dropdown visibility changes
    useEffect(() => {
        if (dropdownOpen) {
            const container = shortcutsContainerRef.current;
            if (!container) return;
            
            // Wait a bit for the dropdown to be fully visible
            setTimeout(() => {
                if (shortcutsPsRef.current) {
                    shortcutsPsRef.current.update();
                } else if (window.PerfectScrollbar) {
                    shortcutsPsRef.current = new window.PerfectScrollbar(container, {
                        wheelPropagation: false,
                        suppressScrollX: true,
                        maxScrollbarLength: 150
                    });
                }
            }, 100);
        }
    }, [dropdownOpen]);

    // Toggle dropdown state when shortcuts dropdown is clicked
    const handleShortcutsToggle = (event) => {
        event.preventDefault();
        setDropdownOpen(prev => !prev);
    };

    // Listen for Bootstrap dropdown events to sync state
    useEffect(() => {
        const shortcutsDropdown = document.getElementById('navbarShortcutsDropdown');
        if (!shortcutsDropdown) return;
        
        const handleShown = () => setDropdownOpen(true);
        const handleHidden = () => setDropdownOpen(false);
        
        shortcutsDropdown.addEventListener('shown.bs.dropdown', handleShown);
        shortcutsDropdown.addEventListener('hidden.bs.dropdown', handleHidden);
        
        return () => {
            shortcutsDropdown.removeEventListener('shown.bs.dropdown', handleShown);
            shortcutsDropdown.removeEventListener('hidden.bs.dropdown', handleHidden);
        };
    }, []);

    // handle click events in the parki settings
    const parkiSettingsClick = (event, uriPath) => {
        event.preventDefault();

        // get idToken from local storage if it exists
        const idToken = localStorage.getItem('idToken');

        // set the url and open a new tab
        let url = PARKI_WEBSITE + '/sso-login?token=' + idToken + '&redirect_to=' + encodeURIComponent(uriPath);
        window.location.href = url;
    };

    // open the Integrations Register inside the app
    const integrationsRegisterClick = (event) => {
        event.preventDefault();
        history.push('/integrations');
    };

    // If not an admin, don't render anything
    if (!user.isEntityAdmin) {
        return null;
    }

    return (
        <li className="nav-item dropdown-shortcuts navbar-dropdown dropdown" id="navbarShortcutsDropdown">
            <a
                className="nav-link dropdown-toggle hide-arrow btn btn-icon btn-text-secondary rounded-pill mx-0"
                href="#"
                onClick={handleShortcutsToggle}
                data-bs-toggle="dropdown"
                data-bs-auto-close="outside">
                <i className="icon-base ti tabler-layout-grid-add icon-22px text-heading"></i>
            </a>
            <div className="dropdown-menu dropdown-menu-end p-0">
                <div className="dropdown-menu-header border-bottom">
                    <div className="dropdown-header d-flex align-items-center py-3">
                        <h6 className="mb-0 me-auto">Parki Settings</h6>
                    </div>
                </div>
                <div 
                    className="dropdown-shortcuts-list scrollable-container"
                    ref={shortcutsContainerRef}
                    style={{ 
                        position: 'relative', 
                        height: '350px',
                        overflow: 'hidden'
                    }}
                >
                    <div className="row row-bordered overflow-visible g-0">
                        {/* User Management */}
                        <div className="dropdown-shortcuts-item col">
                            <span className="dropdown-shortcuts-icon rounded-circle mb-3">
                                <img 
                                    src="https://parki.cloud//wp-content/themes/parki_2.0/img/user_mgmt.svg" 
                                    alt="User Management" 
                                    className="icon-26px"
                                    style={{ maxHeight: '26px', maxWidth: '26px' }}
                                />
                            </span>
                            <a 
                                href="#" 
                                onClick={(e) => parkiSettingsClick(e, 'my-account/user-management/')} 
                                className="stretched-link">
                                User Management
                            </a>
                        </div>

                        {/* Goalseek Periods */}
                        <div className="dropdown-shortcuts-item col">
                            <span className="dropdown-shortcuts-icon rounded-circle mb-3">
                                <img 
                                    src="https://parki.cloud//wp-content/themes/parki_2.0/img/goalseek_2.svg" 
                                    alt="Goalseek Periods" 
                                    className="icon-26px"
                                    style={{ maxHeight: '26px', maxWidth: '26px' }}
                                />
                            </span>
                            <a 
                                href="#" 
                                onClick={(e) => parkiSettingsClick(e, 'my-account/goalseek/')} 
                                className="stretched-link">
                                Goalseek Periods
                            </a>
                        </div>

                        {/* Integrations Register */}
                        <div className="dropdown-shortcuts-item col">
                            <span className="dropdown-shortcuts-icon rounded-circle mb-3">
                                <img 
                                    src="https://parki.cloud//wp-content/themes/parki_2.0/img/onboarding_2.svg" 
                                    alt="Integrations Register" 
                                    className="icon-26px"
                                    style={{ maxHeight: '26px', maxWidth: '26px' }}
                                />
                            </span>
                            <a 
                                href="#" 
                                onClick={integrationsRegisterClick} 
                                className="stretched-link">
                                Integrations Register
                            </a>
                        </div>

                        {/* System Preferences */}
                        <div className="dropdown-shortcuts-item col">
                            <span className="dropdown-shortcuts-icon rounded-circle mb-3">
                                <img 
                                    src="https://parki.cloud//wp-content/themes/parki_2.0/img/sys_pref.svg" 
                                    alt="System Preferences" 
                                    className="icon-26px"
                                    style={{ maxHeight: '26px', maxWidth: '26px' }}
                                />
                            </span>
                            <a 
                                href="#" 
                                onClick={(e) => parkiSettingsClick(e, 'my-account/parki-settings/')} 
                                className="stretched-link">
                                System Preferences
                            </a>
                        </div>

                        {/* Booking Source Grouping */}
                        <div className="dropdown-shortcuts-item col">
                            <span className="dropdown-shortcuts-icon rounded-circle mb-3">
                                <img 
                                    src="https://parki.cloud//wp-content/themes/parki_2.0/img/bking_src_grping.svg" 
                                    alt="Booking Source Grouping" 
                                    className="icon-26px"
                                    style={{ maxHeight: '26px', maxWidth: '26px' }}
                                />
                            </span>
                            <a 
                                href="#" 
                                onClick={(e) => parkiSettingsClick(e, 'my-account/parki-booking-source/')} 
                                className="stretched-link">
                                Booking Source Grouping
                            </a>
                        </div>

                        {/* Chart of Accounts Mapping */}
                        <div className="dropdown-shortcuts-item col">
                            <span className="dropdown-shortcuts-icon rounded-circle mb-3">
                                <img 
                                    src="https://parki.cloud//wp-content/themes/parki_2.0/img/coa_2.svg" 
                                    alt="Chart of Accounts Mapping" 
                                    className="icon-26px"
                                    style={{ maxHeight: '26px', maxWidth: '26px' }}
                                />
                            </span>
                            <a 
                                href="#" 
                                onClick={(e) => parkiSettingsClick(e, 'my-account/benchmark-mapping/')} 
                                className="stretched-link">
                                Chart of Accounts Mapping
                            </a>
                        </div>

                        {/* Social Media Goals */}
                        <div className="dropdown-shortcuts-item col">
                            <span className="dropdown-shortcuts-icon rounded-circle mb-3">
                                <img 
                                    src="https://parki.cloud//wp-content/themes/parki_2.0/img/sm_2.svg" 
                                    alt="Social Media Goals" 
                                    className="icon-26px"
                                    style={{ maxHeight: '26px', maxWidth: '26px' }}
                                />
                            </span>
                            <a 
                                href="#" 
                                onClick={(e) => parkiSettingsClick(e, 'my-account/parki-smm/')} 
                                className="stretched-link">
                                Social Media Goals
                            </a>
                        </div>

                        {/* Peak Periods & Targets */}
                        <div className="dropdown-shortcuts-item col">
                            <span className="dropdown-shortcuts-icon rounded-circle mb-3">
                                <img 
                                    src="https://parki.cloud//wp-content/themes/parki_2.0/img/target_2.svg" 
                                    alt="Peak Periods & Targets" 
                                    className="icon-26px"
                                    style={{ maxHeight: '26px', maxWidth: '26px' }}
                                />
                            </span>
                            <a 
                                href="#" 
                                onClick={(e) => parkiSettingsClick(e, 'my-account/parki-goals/')} 
                                className="stretched-link">
                                Peak Periods & Targets
                            </a>
                        </div>

                        {/* if the entityId is 11 - Housekeeper Names Mapping */}
                        {user.entityId === 11 && (
                            <div className="dropdown-shortcuts-item col">
                                <span className="dropdown-shortcuts-icon rounded-circle mb-3">
                                    <img 
                                        src="https://parki.cloud//wp-content/themes/parki_2.0/img/names_2.svg" 
                                        alt="Housekeeper Names Mapping" 
                                        className="icon-26px"
                                        style={{ maxHeight: '26px', maxWidth: '26px' }}
                                    />
                                </span>
                                <a 
                                    href="#" 
                                    onClick={(e) => parkiSettingsClick(e, 'my-account/housekeeping-names/')} 
                                    className="stretched-link">
                                    Housekeeper Names Mapping
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </li>
    );
};

export default ParkiSettingsDropdown;